<template>
  <Loader v-if="isLoading"></Loader>
  <Alert
    v-if="errorMsgs.length"
    :messages="errorMsgs"
    :dismissible="false"></Alert>

  <div class="b-ticket" :class="className">
    <div v-if="sortedTickets.length">
      <template v-for="(ticket, index) in sortedTickets" :key="index">
        <div
          v-if="ticket.type === '0'"
          class="b-ticket__el-holding"
          :class="isDisabledPointsTickets(ticket)">
          <div>
            <div class="b-ticket__title">{{ ticket.ticketDescription }}</div>
            <div
              class="b-ticket__price"
              :class="ticket.points > 0 ? 'b-ticket__price--withPoints' : ''">
              {{ roundToDigits(ticket.ticketPrice) }} €
              <span v-if="ticket.points > 0">
                {{ dictionary.pointsLabel.replace('{x}', ticket.points) }}
              </span>
            </div>
          </div>
          <div class="b-ticket__counter-wrapper">
            <div>
              <button
                :key="selectedTickets[ticket.ticketId]"
                :disabled="store.getSelectedTicketsCounter(ticket) === 0"
                type="button"
                class="btn-icon btn--minus-icon"
                @click="removeTicket(ticket)"></button>
            </div>
            <div class="b-ticket__counter">
              {{ store.getSelectedTicketsCounter(ticket) }}
            </div>
            <div>
              <button
                type="button"
                class="btn-icon btn-plus-icon"
                :disabled="isDisabledAddTickets(ticket)"
                @click="addTicket(ticket)"></button>
            </div>
          </div>
        </div>

        <div v-if="ticket.type === '1'">
          <CD4UTicket
            :item="ticket"
            :index="index"
            @add-tickets="addCD4UTickets" />

          <CD4UModal
            :item="ticket"
            :show-modal-prop="showCD4UModal[index]"
            :array-index="index"
            @close-modal="onCloseCD4uModal" />
        </div>

        <div v-if="ticket.type === '2'" class="b-ticket__el-holding">
          <div>
            <div class="b-ticket__title">
              {{ dictionary.discountVoucherCode }} #{{ ticket.code }}
            </div>
            <div class="b-ticket__price">{{ ticket.amount }} €</div>
          </div>
          <div class="b-ticket__counter-wrapper">
            <div>
              <button
                :key="index"
                type="button"
                class="btn-icon btn--minus-icon"
                @click="onClickRemoveVoucherCode(ticket)"></button>
            </div>
            <div class="b-ticket__counter">1</div>
            <div></div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import axios from 'axios'
import { getCurrentInstance, nextTick, ref, computed } from 'vue'
import { useBookingStore } from '../../../store/BookingStore'
import CD4UTicket from './CD4UTicket.vue'
import CD4UModal from './CD4UModal.vue'
import { roundToDigits, scrollToError } from '../../../../lib/lib'
import Loader from './../../../components/Shared/Loader.vue'
import Alert from './../../../components/Shared/Alert.vue'
import constants from './../../../../lib/constants'
import * as lib from './../../../../lib/lib'

// emits
const emit = defineEmits(['fetch-tickets'])

const store = useBookingStore()
const { selectedTickets } = store.bookingProgress
// support for up to 5 CD4U modal windows
const showCD4UModal = ref([false, false, false, false, false])
const isLoading = ref(false)
const errorMsgs = ref([])
const { dictionary, configuration } =
  getCurrentInstance().appContext.config.globalProperties

defineProps({
  className: { default: '', type: String },
})

const addTicket = (ticket) => {
  store.addTicket(ticket)
}

const removeTicket = (ticket) => {
  store.removeTicket(ticket)
}

const addCD4UTickets = async (item, index) => {
  showCD4UModal.value[index] = true
}

const onCloseCD4uModal = (index) => {
  showCD4UModal.value[index] = false
}

const onClickRemoveVoucherCode = async (code) => {
  const prepaidLength = constants.vouchers.prepaid.length

  await removeVoucherCode(code)

  // prepaid
  if (code.code.length === prepaidLength) {
    // user has delete all prepaid vouchers
    // fetch tickets again
    if (store.getUserVouchersCodes.length === 0) {
      store.resetVoucherType()
      emit('fetch-tickets')
    }
  }
}

const removeVoucherCode = async (code) => {
  isLoading.value = true

  const postData = {
    code: code.code,
    userSessionId: store.bookingProgress.usersessionId,
  }
  const endpoint = configuration.endpointVouchersRemoveCode
  try {
    const resp = await axios.post(endpoint, postData, lib.axiosConfig)
    const { data } = resp

    if (data.hasError) {
      return handleError(data.errorMessages)
    }

    store.removeVoucherCodeByIndex(code)

    handleSuccess()
  } catch (errors) {
    handleError([dictionary.defaultErrorMessage])
    console.error(errors)
  }
}

const handleSuccess = () => {
  isLoading.value = false
  errorMsgs.value = []
}

const handleError = async (messages) => {
  errorMsgs.value = messages
  isLoading.value = false

  await nextTick()
  scrollToError()
}

const isDisabledAddTickets = (item) => {
  const availablePoints = store.checkAvailablePoints(item.points)
  const sum = store.getSeatsCount + item.seatsCount

  return sum > configuration.maximumTickets || !availablePoints
}

const isDisabledPointsTickets = (ticket) => {
  if (lib.hasValue(ticket)) {
    const availablePoints = store.checkAvailablePoints(ticket.points)

    if (!(ticket.ticketId in selectedTickets) && !availablePoints) {
      return 'b-ticket__el-holding--disabled'
    } else {
      return ''
    }
  }
}

const sortedTickets = computed(() => {
   let allTickets = []

  if (store.getAllTickets.length) {
    allTickets = [...store.getAllTickets]

    if (store.getCD4UTickets.length) {
      store.getCD4UTickets.forEach((obj) => {
        allTickets.push(obj)
      })
    }

    if (store.getUserVouchersCodes.length) {
      store.getUserVouchersCodes.forEach((obj) => {
        allTickets.push(obj)
      })
    }

    allTickets.sort((a, b) => {
      // Compare by active (true first), false is considered greater than true
      if (a.active === b.active) {
        // If both active statuses are the same, compare by ticketPrice (ascending)
        return a.ticketPrice - b.ticketPrice
      }
      return a.active === true ? -1 : 1
    })
  }
  // console.log('alltickets', allTickets)
  return allTickets
})
</script>
